// Common Google Business Profile suspension reasons. Operators search these
// in panic — strong organic intent, highly linkable from forums.

export interface SuspensionReason {
  slug: string;
  name: string;
  /** "Soft" = listing disabled but recoverable; "Hard" = profile removed. */
  severity: "Soft" | "Hard";
  signals: string[];
  description: string;
  remediation: string[];
  prevention: string[];
}

export const SUSPENSIONS: SuspensionReason[] = [
  {
    slug: "keyword-stuffing-in-business-name",
    name: "Keyword stuffing in the business name",
    severity: "Hard",
    signals: [
      "Profile disappears from Maps with no warning email.",
      "Status in GBP dashboard reads 'Suspended'.",
      "Most recent edit added descriptive words to the business name.",
    ],
    description:
      "Adding location, service, or descriptor words to the business name violates Google's representation guidelines. The name must match the real-world signage and legal trading name.",
    remediation: [
      "Revert the business name to the legal/signage name.",
      "Submit a reinstatement request via the GBP Help Center, attaching photos of storefront signage, registration documents, and utility bills.",
      "Expect 3–14 days for review.",
    ],
    prevention: [
      "Never include service descriptors ('Plumbing', 'Best', '24/7') in the name unless they appear on signage and registration.",
      "Push service keywords into the Services / Description fields instead.",
    ],
  },
  {
    slug: "address-violation-service-area-business",
    name: "Address violation (service-area business)",
    severity: "Hard",
    signals: [
      "Service-area business with a visible street address.",
      "Address is a virtual office, coworking suite, P.O. box, or unmanned storefront.",
    ],
    description:
      "Service-area businesses — plumbers, locksmiths, mobile detailers — must hide their address unless they serve customers at the listed location during stated hours.",
    remediation: [
      "Switch the profile to 'I deliver goods and services to my customers' and clear the address.",
      "If the address is a real, staffed storefront, file reinstatement with interior + exterior photos showing branding, staff, and customer-facing signage.",
    ],
    prevention: [
      "Use a service area, not a public address, for SABs.",
      "Never list virtual office addresses or mailbox stores.",
    ],
  },
  {
    slug: "ineligible-business-model",
    name: "Ineligible business model",
    severity: "Hard",
    signals: [
      "Online-only business with no physical service location.",
      "Business is a lead-generation site or affiliate model.",
    ],
    description:
      "Google requires a real-world business that interacts with customers face-to-face — either at a location or at the customer's address.",
    remediation: [
      "If you have any in-person service, document it (photos, contracts, vehicle decals) and appeal.",
      "Pure online-only businesses are not eligible for a GBP listing.",
    ],
    prevention: [
      "Don't list e-commerce-only brands, SaaS products, or affiliate sites.",
    ],
  },
  {
    slug: "duplicate-listing",
    name: "Duplicate listing",
    severity: "Soft",
    signals: [
      "Multiple GBP listings exist for the same business at the same address.",
      "Google merged or suspended the newer of the duplicates.",
    ],
    description:
      "Each real-world location may only have one verified profile. Duplicates arising from rebrands, ownership changes, or unclaimed legacy listings should be merged or removed.",
    remediation: [
      "Identify the canonical listing (oldest, most reviews) and request removal of duplicates via the listing's 'Suggest an edit' > 'Close or remove'.",
      "For ownership conflicts, file a conflict resolution request.",
    ],
    prevention: [
      "Audit duplicates quarterly using Map Maker or a tool like BrightLocal.",
      "Never create a new profile when rebranding — edit the existing profile.",
    ],
  },
  {
    slug: "shared-office-or-virtual-address",
    name: "Shared office / virtual address",
    severity: "Hard",
    signals: [
      "Address is a coworking floor, Regus suite, or virtual office provider.",
      "Multiple businesses verified to the same suite number.",
    ],
    description:
      "Google enforces that the listed address is a real, staffed location open to customers. Shared or virtual addresses are flagged automatically.",
    remediation: [
      "If you genuinely operate from a private, signposted suite within the building, appeal with photos of your suite door, signage, and a lease.",
      "Otherwise, switch to a service-area model and clear the address.",
    ],
    prevention: ["Avoid virtual offices for GBP. Use a service area if you don't have a real storefront."],
  },
  {
    slug: "category-mismatch",
    name: "Primary category mismatch",
    severity: "Soft",
    signals: [
      "Profile is live but ranking has collapsed.",
      "Category chosen does not match the business's actual primary service.",
    ],
    description:
      "While usually not a suspension trigger by itself, choosing a category that misrepresents the business — to chase a higher-volume vertical — can lead to quality-team action and ranking suppression.",
    remediation: [
      "Set the primary category to the most accurate fit.",
      "Use secondary categories for adjacent services.",
    ],
    prevention: ["Always pick the category that best represents what you primarily do."],
  },
  {
    slug: "fake-reviews-or-review-gating",
    name: "Fake reviews or review gating",
    severity: "Hard",
    signals: [
      "Sudden spike in 5-star reviews from new accounts.",
      "Reviews filter known to detect coordinated activity.",
      "Receipt of a policy violation email.",
    ],
    description:
      "Buying reviews, exchanging reviews, or filtering happy customers to Google while routing unhappy ones elsewhere ('review gating') violates Google's review policy.",
    remediation: [
      "Stop the offending campaign immediately.",
      "Audit and remove third-party tools that practise gating.",
      "Appeal with a written remediation plan and documentation of the corrected process.",
    ],
    prevention: [
      "Use review-request tools that send identical requests to all customers regardless of expected sentiment.",
      "Never offer incentives in exchange for reviews.",
    ],
  },
];

export function getSuspensionBySlug(slug: string): SuspensionReason | undefined {
  return SUSPENSIONS.find((s) => s.slug === slug);
}
